import { Modules } from '@enum/central/modules.enum';
import { PlanCapabilitiesService, planoIncluiModulo } from '@shared/services/plan-capabilities.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { EMPTY, Subscription, catchError, exhaustMap, filter, timer } from 'rxjs';
import { TenantPerm } from '@enum/tenant/permission/tenant-perm.enum';
import { PermissionService } from '@shared/services/permission.service';
import { AccountingBadgeService, TenancyService } from '@core/services/common';
import { NotificationRoute } from '@core/services/api/routes/tenancy/notification.route';
import { NotificationCenterComponent } from '../notifications/notification-center.component';
import { TutorialService } from '@core/tutorial/tutorial.service';
import { OnboardingService } from '@core/onboarding/onboarding.service';
import { JornadaService } from '@core/jornada/jornada.service';
import { BuscaGlobalComponent } from '../busca/busca-global.component';
import { BuscaGlobalService } from '../busca/busca-global.service';
import { ResultadoDeBusca } from '../busca/busca-global.model';
import { AccountingFirmLink } from '@core/services/api/routes/tenancy/accounting-firm-links.route';

import { larguraDeDialogo } from '@core/dialogo/largura-de-dialogo';
import { SupportBadgeService, SupportHubService } from 'src/app/views/pages/tenant/support/services';
import { CreditsBalanceComponent } from './credits-balance.component';
/**
 * A barra superior do shell próprio — substitui `kt-header`, `kt-header-mobile`
 * e `kt-topbar`.
 *
 * Ela é enxuta de propósito, porque é o que a antiga fazia DE FATO. Medido no
 * shell do Metronic antes de escrever este:
 *
 *  - `kt-menu-horizontal` renderizava `<ul class="menu-nav">` sobre
 *    `header.items`, que `MenuConfig` declara como `[]` — o menu horizontal era
 *    uma lista vazia. O único conteúdo real dele era o selo de ambiente, REMOVIDO
 *    em 31/08/2026 (decisão do dono): texto branco sobre fundo quase-branco,
 *    ilegível, e sem função no produto;
 *  - a barra de progresso de navegação estava **comentada** no template
 *    (`header.component.html:6`), então o `LoadingBarService` girava sem nada
 *    aparecer;
 *  - `kt-topbar` tinha o bloco de notificações comentado e sobrava o perfil.
 *
 * Recriar a lista vazia e a barra invisível seria inventar comportamento, não
 * preservar.
 */
@Component({
  selector: 'mg-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatToolbarModule,
    MatIconButton,
    // ⚠️ SEM ISTO A BARRA DE BUSCA NASCE COMO <button> CRU.
    //
    // O template escreve `matButton="outlined"` no gatilho da busca. Diretiva
    // usada sem estar em `imports` NAO da erro, NAO da aviso e o build PASSA:
    // o Angular trata o atributo como um atributo comum qualquer. O resultado e
    // um `<button>` sem estilo nenhum — `display: block`, `appearance: auto`,
    // borda `outset` do navegador — e, por causa do `text-align: center` da
    // folha do agente do usuario, o conteudo sai AMONTOADO NO MEIO de uma caixa
    // de 460px. Medido em Chrome real contra o QA em 21/09/2026.
    MatButton,
    MatIcon,
    RouterLink,
    MatBadge,
    MatTooltip,
    CreditsBalanceComponent,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  /** Pedido de abertura da gaveta no mobile. Quem guarda o estado é a casca. */
  @Output() abrirMenu = new EventEmitter<void>();

  private readonly dialogo = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly busca = inject(BuscaGlobalService);
  private readonly permissoes = inject(PermissionService);
  private readonly tenancy = inject(TenancyService);
  private readonly notificacoesApi = inject(NotificationRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly accountingBadge = inject(AccountingBadgeService);
  private readonly supportBadge = inject(SupportBadgeService);
  /** Estado do painel "Ajuda e suporte" (spec §6.1) — o template só lê `hub.*`, quem abre/fecha é o próprio painel. */
  protected readonly hub = inject(SupportHubService);

  /**
   * O motor do tutorial. É `protected` e não `private` porque o template chama
   * dois MÉTODOS dele direto (`temTourNaRota`, `iniciarTourDaRota`) — e são
   * métodos, e não signals, de propósito: assim o menu de ajuda continua
   * montável com o serviço mockado, que é como todo spec desta barra o recebe.
   *
   * Injetá-lo aqui é também o que CONSTRÓI o serviço: é ele quem observa a rota
   * e dispara o tour da primeira visita, e um serviço `root` só nasce quando
   * alguém o pede. A barra superior está em toda tela de `/painel`.
   */
  protected readonly tutorial = inject(TutorialService);

  /**
   * A Órbita Financeira. Injetar aqui é o que CONSTRÓI o serviço, como o
   * tutorial: é ele quem assina o fim do tour do painel e observa a rota, e um
   * serviço `root` só nasce quando alguém o pede. `protected` porque o
   * template lê `idsNovos()` (o ponto de novidade) e chama `abrir()`.
   */
  protected readonly jornada = inject(JornadaService);
  protected readonly onboarding = inject(OnboardingService);
  private readonly plano = inject(PlanCapabilitiesService);
  protected get orbitaIncluida(): boolean { return planoIncluiModulo(this.plano.modulos, Modules.FINANCIAL_ORBIT); }

  /** Casca do tenant ou do admin — o mesmo critério usado pelo aside. */
  protected readonly ehTenant = this.tenancy.isSubdomain;

  /** Uma paleta por vez. Duas empilhadas é o defeito clássico deste padrão. */
  private aberta?: MatDialogRef<BuscaGlobalComponent, ResultadoDeBusca>;

  /** O servidor filtra cada categoria pela permissão correspondente antes de contar. */
  protected readonly podeVerNotificacoes = this.ehTenant && this.permissoes.checkPermissions([
    TenantPerm.Accounts_View, TenantPerm.Dashboard_Cashflow, TenantPerm.FiscalRps_View, TenantPerm.Governance_View,
  ]);
  protected naoLidas = 0;
  protected notificacoesAbertas = false;
  private painelNotificacoes?: MatDialogRef<NotificationCenterComponent>;

  protected vinculosContabeis: AccountingFirmLink[] = [];

  private readonly inscricoes = new Subscription();

  ngOnInit(): void {
    // O badge de contabilidade vem ANTES do gate, e é UMA chamada só. Ele não
    // tem nada a ver com `Accounts_View`: enquanto a chamada morava nos dois
    // ramos do `if`, apagar a do ramo de baixo mantinha a suíte verde e sumia
    // com o badge para todo usuário que TEM a permissão do sino — a maioria.
    this.carregarVinculosContabeis();

    if (this.ehTenant) {
      // Alimenta os dois contadores (`SupportHubService.contagem`, um
      // `computed` sobre os observables deste service): chamados com
      // resposta nova e novidades não vistas. O painel "Ajuda e suporte" lê
      // o resultado por injeção — não há assinatura própria aqui.
      this.supportBadge.refresh();
    }

    if (!this.podeVerNotificacoes) {
      return;
    }

    this.atualizarContagemNotificacoes();
    this.inscricoes.add(timer(60_000, 60_000).pipe(
      filter(() => document.visibilityState === 'visible'),
      exhaustMap(() => this.notificacoesApi.center('unread', 1, 1).pipe(catchError(() => EMPTY))),
    ).subscribe(resposta => {
      this.naoLidas = resposta.meta.unread_count;
      this.cdr.markForCheck();
    }));
  }

  /**
   * O `error` NÃO é redundância do `catchError` do `AccountingBadgeService`.
   * Esta barra aparece em TODA tela do tenant: se um dia o serviço deixar de
   * absorver a falha, `subscribe` sem ramo de erro faz o RxJS relançar num
   * `setTimeout` — e o estouro cai numa tela que não pediu nada. Aqui o
   * cabeçalho simplesmente fica sem badge.
   */
  private carregarVinculosContabeis(): void {
    this.inscricoes.add(
      this.accountingBadge.getActiveAccountingFirmLinks().subscribe({
        next: vinculos => {
          this.vinculosContabeis = vinculos;
          this.cdr.markForCheck();
        },
        error: () => {
          this.vinculosContabeis = [];
          this.cdr.markForCheck();
        },
      })
    );
  }

  /**
   * Os nomes dos escritórios ATIVOS, separados por vírgula.
   *
   * A lista inteira, e não `vinculosContabeis[0]`: uma empresa pode ter mais
   * de um escritório com acesso, e mostrar um só esconderia o outro — a mesma
   * mentira que mostrar um vínculo `pending` seria.
   */
  protected get nomesDosEscritorios(): string {
    return this.vinculosContabeis.map(vinculo => vinculo.accounting_firm_name).join(', ');
  }

  /**
   * O que o badge significa, dito por extenso — para leitor de tela
   * (`aria-label`) e para quem passa o mouse (`matTooltip`). O rótulo visível
   * "Contabilidade" cabe na barra; a frase inteira, não.
   */
  protected get rotuloAcessivelDaContabilidade(): string {
    return `${this.nomesDosEscritorios} tem acesso aos dados desta empresa`;
  }

  ngOnDestroy(): void {
    this.inscricoes.unsubscribe();
    this.painelNotificacoes?.close();
  }

  /**
   * `Ctrl+K` (e `Cmd+K` no macOS) é o atalho consagrado de paleta de comando,
   * e é REQUISITO DE FORMA do dono: "sem ele é só um campo a mais".
   *
   * `preventDefault` não é opcional: no Chrome, `Ctrl+K` leva o foco para a
   * barra de endereço; sem impedir, a paleta abriria atrás de um campo que
   * acabou de roubar o teclado.
   *
   * Não há guarda de "estou digitando num input": `Ctrl+K` dentro de um campo
   * de formulário não é atalho de nenhum campo nosso, e o usuário que o
   * aperta ali quer a paleta.
   *
   * Medido antes de escolher a tecla: este é o ÚNICO listener global de
   * teclado do produto (zero ocorrências de `document:keydown`/`window:keydown`
   * em `src/app` antes desta tarefa).
   */
  @HostListener('document:keydown', ['$event'])
  aoTeclarNoDocumento(evento: KeyboardEvent): void {
    if (evento.key?.toLowerCase() !== 'k' || !(evento.ctrlKey || evento.metaKey)) {
      return;
    }

    evento.preventDefault();
    this.abrirBusca();
  }

  abrirBusca(): void {
    if (this.aberta) {
      return;
    }

    // Lei 1: tamanho e POSIÇÃO vêm da config do Material, não de CSS nosso.
    // `top: 10vh` é a ancoragem no terço superior da referência.
    this.aberta = this.dialogo.open(BuscaGlobalComponent, {
      panelClass: 'mg-paleta',
      ...larguraDeDialogo('M'),
      position: { top: '10vh' },
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      ariaLabel: 'Busca global',
    });

    this.aberta.afterClosed().subscribe(escolha => {
      this.aberta = undefined;
      this.aoEscolher(escolha);
    });
  }

  /** O destino conhece a permissão de escrita (segundo gate); a paleta só devolve a escolha. */
  protected aoEscolher(escolha?: ResultadoDeBusca): void {
    if (!escolha) {
      return;
    }

    void this.router.navigateByUrl(this.busca.rotaDe(escolha));
  }

  protected abrirNotificacoes(): void {
    if (!this.podeVerNotificacoes || this.painelNotificacoes) return;
    this.notificacoesAbertas = true;
    this.painelNotificacoes = this.dialogo.open(NotificationCenterComponent, {
      ...larguraDeDialogo('P'),
      height: '100dvh',
      maxHeight: '100dvh',
      position: { right: '0', top: '0' },
      panelClass: 'mg-central-notificacoes',
      ariaLabelledBy: 'central-notificacoes-titulo',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    });
    this.inscricoes.add(this.painelNotificacoes.afterClosed().subscribe(() => {
      this.painelNotificacoes = undefined;
      this.notificacoesAbertas = false;
      this.atualizarContagemNotificacoes();
      this.cdr.markForCheck();
    }));
  }

  private atualizarContagemNotificacoes(): void {
    this.inscricoes.add(this.notificacoesApi.center('unread', 1, 1).subscribe({
      next: resposta => {
        this.naoLidas = resposta.meta.unread_count;
        this.cdr.markForCheck();
      },
      // O painel tem retry e erro visível. Falha de badge não interrompe a barra.
      error: () => undefined,
    }));
  }

  /**
   * `aria-label` do gatilho único de "Ajuda e suporte" (spec §6.1): sem
   * pendência é só o nome; com pendência, diz quanto e o quê, no singular ou
   * plural certo, e omite a parte zerada (2 chamados OU 1 novidade OU as
   * duas juntas, nunca "0 novidades").
   */
  protected rotuloDoGatilhoDeSuporte(): string {
    const chamados = this.hub.chamadosNaoLidos();
    const novidades = this.hub.novidadesNaoVistas();

    if (chamados === 0 && novidades === 0) {
      return 'Ajuda e suporte';
    }

    const partes: string[] = [];
    if (chamados > 0) {
      partes.push(`${chamados} ${chamados === 1 ? 'chamado com resposta nova' : 'chamados com resposta nova'}`);
    }
    if (novidades > 0) {
      partes.push(`${novidades} ${novidades === 1 ? 'novidade' : 'novidades'}`);
    }

    return `Ajuda e suporte, ${partes.join(' e ')}`;
  }
}
